import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Dialog,
  Modal,
  ProgressBar,
  Spinner,
  Stack,
  Text,
  useWizard,
  WizardContext,
  WizardProps,
  WizardState
} from '@efast_public/fjd-component-library';
import { BehApiRoutes, behClient } from '../../../api/beh-api';
import { ConfigurationStepDto } from '../../../api/hooks/useConfigurationStep';
import { useMutation } from '@tanstack/react-query';
import { getConfigurationStepFromIndex } from '../ConfigWizard.helper';

export const defaultWizardState: WizardState = {
  currentPageIndex: 0,
  pagesLength: 0,
  isSubmitting: false,
  progress: 0
};

const calculateProgress = (childs: unknown[], currentChild: number = 0) =>
  (MAX_PROGRESS * (currentChild + 1)) / childs.length;

const MAX_PROGRESS = 100;
export function ConfigWizard(props: WizardProps) {
  const [progress, setProgress] = useState<number>(
    calculateProgress(props.children, props.startOnPageIndex)
  );

  const { mutateAsync: updateConfigurationStep } = useMutation({
    mutationFn: ({ payload }: { payload: ConfigurationStepDto }) =>
      behClient.patch(BehApiRoutes.configurationStep(), payload)
  });

  const {
    wizardContextApi,
    pageUp,
    pageDown,
    primaryButtonLabel,
    secondaryButtonLabel,
    pageConfig,
    wizardState,
    setWizardState,
    lastPageIndex
  } = useWizard(props);

  useEffect(() => {
    if (progress >= MAX_PROGRESS) {
      return;
    }

    const newProgress = calculateProgress(
      props.children,
      wizardState.currentPageIndex
    );
    if (newProgress !== progress) {
      setProgress(newProgress);
      const step = getConfigurationStepFromIndex(wizardState.currentPageIndex);
      if (step) {
        updateConfigurationStep({
          payload: { configurationStep: step }
        }).catch(() => {});
      }
    }
  }, [progress, props.children, wizardState, updateConfigurationStep]);

  const isCloseButtonVisible = wizardState.currentPageIndex > 0;

  return (
    <WizardContext.Provider value={wizardContextApi}>
      <Modal
        windowSize={props.size}
        closeOnBackdropClick={props.closeOnBackdropClick}
        open={props.isOpen}
        onClose={undefined}
      >
        <Modal.Header onClose={undefined} id="wizard">
          {wizardState.headline ?? props.defaultHeading}
        </Modal.Header>
        <ProgressBar
          color={'primary-600'}
          value={progress}
          max={MAX_PROGRESS}
        />
        <Modal.Content>
          {props.children[wizardState.currentPageIndex]}
        </Modal.Content>
        <Modal.Footer>
          <Stack
            style={{ widht: '100%' }}
            width="100%"
            justifyContent="space-between"
            direction={isCloseButtonVisible ? 'row' : 'row-reverse'}
          >
            {isCloseButtonVisible && (
              <Button variant="secondary-outline" onClick={pageDown}>
                {secondaryButtonLabel}
              </Button>
            )}

            <Stack direction="row" spacing="s">
              {wizardState.isStepOptional && (
                <Button
                  onClick={() => pageUp({ shouldSkipSubmition: true })}
                  variant="primary-text"
                >
                  {wizardState.skipButtonLabel}
                </Button>
              )}
              <Button
                variant="primary"
                disabled={
                  !(pageConfig.isFormValid ?? true) || wizardState.isSubmitting
                }
                onClick={() => {
                  if (wizardState.currentPageIndex === lastPageIndex) {
                    setWizardState(defaultWizardState);
                    if (pageConfig.onSubmit) {
                      pageConfig
                        .onSubmit()
                        .then(() => {
                          props.onConfirmed();
                        })
                        .catch(() => {});
                    } else {
                      props.onConfirmed();
                    }
                  } else {
                    pageUp();
                  }
                }}
              >
                {wizardState.isSubmitting ? (
                  <Spinner size="sm" />
                ) : (
                  primaryButtonLabel
                )}
              </Button>
            </Stack>
          </Stack>
        </Modal.Footer>
      </Modal>
    </WizardContext.Provider>
  );
}
